import { indexOf, omit, xor } from 'lodash';

import { Action } from '../actions';
import {
    CHANGE_BANNER_IMAGE_CREATIVE_UPLOADING_LINK,
    CLOSE_BANNER_IMAGE_CANVAS,
    SHOW_BANNER_IMAGE_CANVAS,
    UPLOAD_AD_BANNER_IMAGES,
    UPLOAD_BANNER_IMAGE_CREATIVE_BY_LINK
} from '../actions/banners-edit/banner-image';
import {
    ADD_IMAGES_TO_NEW_BANNERS,
    CANCEL_UPLOAD_BANNER_IMAGE,
    CANCEL_UPLOAD_IMAGE_CREATIVE,
    CLEAR_IMAGE_EDITOR_DATA,
    CLOSE_IMAGE_CROP,
    CLOSE_IMAGES_GALLERY,
    ON_ACCEPT_BULK_IMAGE_REPLACEMENT,
    REMOVE_BANNER_IMAGE,
    RESET_BANNER_IMAGE_CREATIVE_MODE,
    RESET_BANNER_IMAGE_UPLOAD_ERROR,
    SELECT_IMAGES_IN_GALLERY,
    SELECT_ONE_IMAGE_IN_GALLERY,
    SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR,
    SHOW_BANNER_IMAGE_LINK_INPUT,
    SHOW_BANNER_IMAGE_UPLOAD_ERROR,
    SHOW_IMAGE_GALLERY,
    SHOW_IMAGE_GALLERY_ERROR,
    UPDATE_BANNER_IMAGE,
    UPDATE_BANNER_IMAGE_BY_GALLERY,
    UPDATE_BANNER_IMAGE_BY_LINK,
    UPDATE_BANNER_IMAGE_CREATIVE,
    UPDATE_IMAGE_EDITOR,
    UPDATE_IMAGES_FOR_GALLERY_EDIT,
    UpdateBannerImage,
    UPLOAD_BANNER_IMAGE,
    UPLOAD_BANNER_IMAGE_BY_LINK
} from '../actions/banners-edit/banner-text-image';
import {
    ACCEPT_MASS_ACTIONS_BULK_IMAGE_CREATIVE_REPLACEMENT_EDITOR,
    CLOSE_MASS_ACTIONS_BULK_IMAGE_CREATIVE_REPLACEMENT_EDITOR
} from '../actions/banners-edit/banners-edit-mass-actions';
import {
    ADD_NEW_TEXT_BANNERS,
    AddNewTextBannersAction,
    CLEAR_BANNERS,
    COPY_BANNER,
    CopyBanner,
    CREATE_NEW_MC_BANNER,
    CREATE_NEW_TEXT_BANNER,
    CreateNewMcBannerAction,
    CreateNewTextBannerAction,
    DELETE_BANNERS,
    RESET_BANNERS_EDIT,
    SAVE_BANNERS_SUCCESS,
    SaveBannersSuccess,
    UPDATE_BANNER_EDIT_IMAGE_FORMATS,
    UpdateBannerEditImageFormatsAction
} from '../actions/bannersEdit';
import { BulkReplacementRadio } from '../components/BulkReplacementModeChooser/BulkReplacementModeChooser';
import { getEditedBannerImage } from '../mappers/banners-edit/api-data-mappers';
import { selectImageCreativeReplacement } from '../selectors/bannerEdit/massActions/massActions';
import {
    imageGalleryTextErrorMapper,
    imageUploadTextErrorMapper
} from '../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { isBannerTextMassActionsAvailable } from '../service/BannersEditMassActionsHelpers/BannersEditMassActionsHelpers';
import { getUploadingImagesHash } from '../service/ImageHelpers/ImageHelpers';
import {
    BannerId,
    BannersToClear,
    ImageEditorId,
    ImageItem,
    ImageOrCreativeUploadingMode,
    SaveBannersResultData
} from '../typings/banners';
import {
    EditedBannerImage,
    getEditedBannerWithImage,
    MassActionId
} from '../typings/bannersEditState';
import { ValidationErrorData, ValidationResultItem } from '../typings/common';
import { BannerImageForEdit } from '../typings/domain';
import { ImageEditorsState, ImageEditorState, State } from '../typings/state';

import { ImageInitialState } from './bannersEdit';

export const ImageEditorInitialState: ImageEditorsState = {
    [MassActionId.bannersEdit]: getImageEditorForMassActionsInitialState()
};

export function getImageEditorForMassActionsInitialState(): ImageEditorState {
    return {
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        imageUploadingLink: '',
        image: ImageInitialState,
        isImageCanvasOpen: false,
        isCropOpen: false,
        isNew: true,
        canvasPageType: undefined
    };
}

export function getBannerImageUploadedState(
    image: BannerImageForEdit | ImageItem
): EditedBannerImage {
    return {
        imageUrl: image.url,
        uploadProgress: 0,
        data: image,
        mode: ImageOrCreativeUploadingMode.UPLOADED,
        displayName: image.name,
        fixedSize: {
            width: image.width,
            height: image.height
        }
    };
}

export function updateBannerImage(
    state: ImageEditorsState,
    action: UpdateBannerImage
): ImageEditorsState {
    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            image: {
                ...state[action.bannerId].image,
                ...getBannerImageUploadedState(action.image)
            },
            imageUploadingLink: ''
        }
    };
}

function updateBannerEditImageFormatsReducer(
    state: ImageEditorsState,
    action: UpdateBannerEditImageFormatsAction,
    fullState: State
): ImageEditorsState {
    const banner = getEditedBannerWithImage(
        fullState.editBanners.banners[action.bannerId]
    );
    if (!banner) {
        return state;
    }
    const data = state[action.bannerId].image.data;

    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            image: {
                ...state[action.bannerId].image,
                data:
                    data !== undefined
                        ? {
                              ...data,
                              formats: action.payload
                          }
                        : undefined
            }
        }
    };
}

const removeBannerImages = (
    state: ImageEditorsState,
    bannerIds: BannerId[]
): ImageEditorsState => {
    const result = bannerIds.reduce((acc, id) => {
        return {
            [id]: {
                ...state[id],
                image: {
                    fixedSize: state[id].image.fixedSize,
                    ...ImageInitialState
                }
            }
        };
    }, {});

    return {
        ...state,
        ...result
    };
};

const resetBannerImage = (
    state: ImageEditorsState,
    bannersToClear: BannersToClear
): ImageEditorsState => {
    const result = Object.keys(state).reduce(
        (acc: ImageEditorsState, bannerId): ImageEditorsState => {
            let nextImageEditorState = state[bannerId] as ImageEditorState;
            const bannerToClearOptions = bannersToClear[bannerId];

            if (bannerToClearOptions) {
                let imageData;
                let fixedSize;
                if (nextImageEditorState.image) {
                    imageData = nextImageEditorState.image.data;
                    fixedSize = nextImageEditorState.image.fixedSize;
                }

                if (bannerToClearOptions.keepBannerImageSize) {
                    const clearedImageState = getImageEditorForMassActionsInitialState();

                    nextImageEditorState = {
                        ...clearedImageState,
                        image: {
                            ...clearedImageState.image,
                            fixedSize,
                            data:
                                imageData !== undefined
                                    ? {
                                          name: '',
                                          url: '',
                                          type: imageData.type,
                                          width: imageData.width,
                                          height: imageData.height
                                      }
                                    : undefined
                        }
                    };
                } else {
                    nextImageEditorState = getImageEditorForMassActionsInitialState();
                }
            }

            return { ...acc, [bannerId]: nextImageEditorState };
        },
        {} as ImageEditorsState
    );

    return result;
};

const deleteBannerImage = (
    state: ImageEditorsState,
    bannerIds: BannerId[],
    fullState: State
): ImageEditorsState => {
    const bannersIdsForDelete = bannerIds.filter(
        id => fullState.editBanners.banners[id].markedForDeletion !== true
    );

    const result = omit(state, bannersIdsForDelete);

    return result;
};

function replaceOrDeleteImages(
    state: ImageEditorsState,
    fullState: State
): ImageEditorsState {
    const { banners } = fullState.editBanners;
    const {
        replaceOrDeleteToggle
    } = fullState.bannersEditMassActions.bannersEdit.data;
    const selectedImage = state[MassActionId.bannersEdit].image;
    const updatedImageEditor = Object.keys(state).reduce((acc, bannerId) => {
        acc[bannerId] = {
            ...state[bannerId]
        };

        const isMassActionAvailable =
            bannerId !== MassActionId.bannersEdit &&
            isBannerTextMassActionsAvailable(banners[bannerId]);

        if (isMassActionAvailable) {
            if (replaceOrDeleteToggle === BulkReplacementRadio.REPLACE) {
                acc[bannerId].image = selectedImage;
            } else {
                acc[bannerId].image = {
                    fixedSize: state[bannerId].image.fixedSize,
                    ...ImageInitialState
                };
            }
        }

        return acc;
    }, {});

    return {
        ...state,
        ...updatedImageEditor,
        [MassActionId.bannersEdit]: {
            ...getImageEditorForMassActionsInitialState()
        }
    };
}

function addNewTextBanners(
    state: ImageEditorsState,
    action: AddNewTextBannersAction
): ImageEditorsState {
    const result: Record<
        ImageEditorId,
        ImageEditorState
    > = action.banners.reduce(
        (data: Record<ImageEditorId, ImageEditorState>, banner) => {
            data[banner.bannerData.id] = {
                image: getEditedBannerImage(),
                isImageCanvasOpen: false,
                imageGallery: {
                    images: undefined,
                    isImageGalleryOpen: false,
                    selectedImageHashFromGallery: []
                },
                isNew: true,
                imageUploadingLink: ''
            };

            return data;
        },
        {}
    );

    return {
        ...state,
        ...result
    };
}

function copyBannerImageInfoReducer(
    state: ImageEditorsState,
    action: CopyBanner
): ImageEditorsState {
    const imageInfo = { ...state[action.bannerId] };

    if (!imageInfo) {
        return state;
    }

    return {
        ...state,
        [action.newBannerId]: imageInfo
    };
}

function createNewImagesTextBanners(
    state: ImageEditorsState,
    action: CreateNewTextBannerAction | CreateNewMcBannerAction
): ImageEditorsState {
    return {
        ...state,
        [action.bannerId]: {
            image: getEditedBannerImage(),
            isImageCanvasOpen: false,
            imageGallery: {
                images: undefined,
                isImageGalleryOpen: false,
                selectedImageHashFromGallery: []
            },
            imageUploadingLink: ''
        }
    };
}

const setSavedImages = (
    state: ImageEditorsState,
    action: SaveBannersSuccess
): ImageEditorsState => {
    const { added, mcBannerAdded } = action.result;
    let imageState = state;

    [added, mcBannerAdded].forEach((addedBanners: SaveBannersResultData) => {
        if (!addedBanners.banners.length) {
            return;
        }

        addedBanners.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }
            const oldId = addedBanners.ids[index];
            const newId = String(addedBanner.id);

            imageState = {
                ...omit(imageState, oldId),
                [newId]: {
                    ...imageState[oldId]
                }
            };
        });

        state = imageState;
    });

    return state;
};

export function imageEditorReducer(
    state: ImageEditorsState = ImageEditorInitialState,
    action: Action,
    fullState: State
): ImageEditorsState {
    switch (action.type) {
        case CLOSE_IMAGE_CROP:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isCropOpen: false
                }
            };
        case RESET_BANNERS_EDIT:
            return ImageEditorInitialState;
        case ADD_NEW_TEXT_BANNERS:
            return addNewTextBanners(state, action);
        case COPY_BANNER:
            return copyBannerImageInfoReducer(state, action);
        case UPLOAD_BANNER_IMAGE:
        case UPLOAD_AD_BANNER_IMAGES:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: action.isLocalUploading
                            ? ImageOrCreativeUploadingMode.UPLOADING_LOCAL
                            : ImageOrCreativeUploadingMode.UPLOADING,
                        uploadingData: getUploadingImagesHash(
                            action.filesToUpload
                        ),
                        errors: undefined
                    }
                }
            };

        case UPDATE_IMAGE_EDITOR:
            return {
                ...action.data,
                ...ImageEditorInitialState
            };

        case CLEAR_IMAGE_EDITOR_DATA:
            return {
                ...state,
                [action.bannerId]: getImageEditorForMassActionsInitialState()
            };

        case SHOW_BANNER_IMAGE_LINK_INPUT:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: ImageOrCreativeUploadingMode.ENTER_LINK,
                        errors: undefined
                    }
                }
            };

        case CANCEL_UPLOAD_BANNER_IMAGE:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: ImageOrCreativeUploadingMode.EMPTY,
                        displayName: '',
                        uploadingData: undefined,
                        errors: undefined
                    }
                }
            };

        case CANCEL_UPLOAD_IMAGE_CREATIVE:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: ImageOrCreativeUploadingMode.EMPTY,
                        errors: undefined
                    }
                }
            };

        case SHOW_BANNER_IMAGE_UPLOAD_ERROR:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: action.isUploadByLink
                            ? ImageOrCreativeUploadingMode.ENTER_LINK
                            : ImageOrCreativeUploadingMode.ERROR,
                        displayName: '',
                        uploadingData: undefined,
                        uploadProgress: 0,
                        errors: action.errors.map(error => {
                            return {
                                code: error.code,
                                text: imageUploadTextErrorMapper(error)
                            };
                        })
                    },
                    isImageCanvasOpen: false
                }
            };

        case RESET_BANNER_IMAGE_CREATIVE_MODE:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: ImageOrCreativeUploadingMode.EMPTY
                    }
                }
            };

        case SHOW_BANNER_IMAGE_CREATIVE_UPLOAD_ERROR:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        mode: action.isUploadByLink
                            ? ImageOrCreativeUploadingMode.ENTER_LINK
                            : ImageOrCreativeUploadingMode.ERROR,
                        uploadProgress: 0,
                        errors: action.errors.map(error => {
                            return {
                                code: error.code,
                                text: imageUploadTextErrorMapper(error)
                            };
                        })
                    },
                    isImageCanvasOpen: false
                }
            };

        case CHANGE_BANNER_IMAGE_CREATIVE_UPLOADING_LINK:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        errors: undefined
                    },
                    imageUploadingLink: action.link
                }
            };

        case RESET_BANNER_IMAGE_UPLOAD_ERROR:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        fixedSize: state[action.bannerId].image.fixedSize,
                        mode:
                            state[action.bannerId].image.mode ===
                            ImageOrCreativeUploadingMode.ENTER_LINK
                                ? ImageOrCreativeUploadingMode.ENTER_LINK
                                : ImageInitialState.mode,
                        errors: undefined
                    }
                }
            };

        case UPDATE_BANNER_IMAGE:
            return updateBannerImage(state, action);

        case ADD_IMAGES_TO_NEW_BANNERS: {
            const bannerIds = Object.keys(fullState.editBanners.banners);
            const newBannerIds = bannerIds.slice(
                bannerIds.length - action.newImages.length
            );
            const newBannerImages = newBannerIds.reduce(
                (acc: ImageEditorsState, bannerId, index) => {
                    acc[bannerId] = {
                        ...getImageEditorForMassActionsInitialState(),
                        image: getBannerImageUploadedState(
                            action.newImages[index]
                        )
                    };

                    return acc;
                },
                {}
            );

            return {
                ...state,
                ...newBannerImages
            };
        }

        case UPLOAD_BANNER_IMAGE_BY_LINK:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        uploadProgress: 1,
                        mode: ImageOrCreativeUploadingMode.UPLOADING,
                        errors: undefined
                    }
                }
            };

        case UPLOAD_BANNER_IMAGE_CREATIVE_BY_LINK:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        uploadProgress: 1,
                        mode: ImageOrCreativeUploadingMode.UPLOADING,
                        errors: undefined
                    }
                }
            };

        case UPDATE_BANNER_IMAGE_BY_LINK:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        imageLinkToCrop: action.link,
                        uploadProgress: 0,
                        mode: ImageOrCreativeUploadingMode.EMPTY,
                        displayNameToCrop: action.name
                    },
                    isCropOpen: action.isCropNeeded
                }
            };

        case UPDATE_BANNER_EDIT_IMAGE_FORMATS:
            return updateBannerEditImageFormatsReducer(
                state,
                action,
                fullState
            );

        case REMOVE_BANNER_IMAGE:
            return removeBannerImages(state, [action.bannerId]);

        case UPDATE_BANNER_IMAGE_BY_GALLERY:
            const { images } = state[action.bannerId].imageGallery;
            const image =
                images !== undefined
                    ? images.find(item => item.hash === action.imagesHash[0])
                    : undefined;

            return image !== undefined
                ? {
                      ...state,
                      [action.bannerId]: {
                          ...state[action.bannerId],
                          imageGallery: {
                              ...state[action.bannerId].imageGallery,
                              isImageGalleryOpen: false,
                              selectedImageHashFromGallery: [],
                              errors: undefined
                          },
                          image: {
                              ...state[action.bannerId].image,
                              imageUrl: image.url,
                              uploadProgress: 0,
                              data: {
                                  ...image,
                                  type: action.bannerType
                              },
                              mode: ImageOrCreativeUploadingMode.UPLOADED,
                              displayName: image.name,
                              fixedSize: {
                                  width: image.width,
                                  height: image.height
                              },
                              errors: undefined
                          }
                      }
                  }
                : state;

        case SELECT_ONE_IMAGE_IN_GALLERY:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        selectedImageHashFromGallery: [action.imageHash]
                    }
                }
            };

        // xor здесь используется для исключения элемента из массива, если чекнули имеющийся в массиве выбранных элементов элемент
        // пример - были выбран [1, 2, 3], хотим отчекнуть 1, получается _xor([1, 2, 3], [1]) = [2, 3]
        case SELECT_IMAGES_IN_GALLERY:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        selectedImageHashFromGallery:
                            indexOf(
                                state[action.bannerId].imageGallery
                                    .selectedImageHashFromGallery,
                                action.imageHash
                            ) !== -1
                                ? xor(
                                      state[action.bannerId].imageGallery
                                          .selectedImageHashFromGallery,
                                      [action.imageHash]
                                  )
                                : [
                                      ...state[action.bannerId].imageGallery
                                          .selectedImageHashFromGallery,
                                      action.imageHash
                                  ]
                    }
                }
            };

        case SHOW_IMAGE_GALLERY:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        isImageGalleryOpen: true,
                        selectedImageHashFromGallery: []
                    }
                }
            };

        case UPDATE_IMAGES_FOR_GALLERY_EDIT:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        images: action.images
                    }
                }
            };

        case CLOSE_IMAGES_GALLERY:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        images: undefined,
                        isImageGalleryOpen: false,
                        selectedImageHashFromGallery: [],
                        errors: undefined
                    }
                }
            };

        case SHOW_IMAGE_GALLERY_ERROR:
            const errors = action.errors.map(
                (error: ValidationResultItem): ValidationErrorData => ({
                    code: error.code,
                    path: error.path,
                    text: imageGalleryTextErrorMapper(
                        error as ValidationResultItem
                    )
                })
            );

            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    imageGallery: {
                        ...state[action.bannerId].imageGallery,
                        errors
                    }
                }
            };

        case UPDATE_BANNER_IMAGE_CREATIVE:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    image: {
                        ...state[action.bannerId].image,
                        imageUrl: action.image.url,
                        uploadProgress: 0,
                        data: action.image,
                        mode: ImageOrCreativeUploadingMode.UPLOADED,
                        displayName: action.image.name,
                        fixedSize: {
                            height: action.image.height,
                            width: action.image.width,
                            isAdaptive: action.image.isAdaptive
                        },
                        errors: undefined
                    },
                    isImageCanvasOpen: false
                }
            };

        case SHOW_BANNER_IMAGE_CANVAS:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isImageCanvasOpen: true,
                    canvasPageType: action.canvasType
                }
            };

        case CLOSE_BANNER_IMAGE_CANVAS:
            return {
                ...state,
                [action.bannerId]: {
                    ...state[action.bannerId],
                    isImageCanvasOpen: false
                }
            };

        case ON_ACCEPT_BULK_IMAGE_REPLACEMENT:
            return replaceOrDeleteImages(state, fullState);
        case ACCEPT_MASS_ACTIONS_BULK_IMAGE_CREATIVE_REPLACEMENT_EDITOR:
            return {
                ...selectImageCreativeReplacement(fullState),
                [MassActionId.bannersEdit]: getImageEditorForMassActionsInitialState()
            };
        case CLEAR_BANNERS:
            return resetBannerImage(state, action.bannersToClear);
        case DELETE_BANNERS:
            return deleteBannerImage(state, action.bannersIds, fullState);
        case CLOSE_MASS_ACTIONS_BULK_IMAGE_CREATIVE_REPLACEMENT_EDITOR:
            return {
                ...state,
                [action.massActionId]: getImageEditorForMassActionsInitialState()
            };
        case CREATE_NEW_TEXT_BANNER:
        case CREATE_NEW_MC_BANNER:
            return createNewImagesTextBanners(state, action);
        case SAVE_BANNERS_SUCCESS:
            return setSavedImages(state, action);
        default:
            return state;
    }
}
